import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { C, MI, mix } from './clockInTheme';
import { WORK_LOCATIONS, WORK_LOCATION_LABELS, type WorkLocation } from '../../constants/work-locations';
import { formatElapsed, getGPS, useShiftElapsed } from '../../lib/shift-clock';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { jobLabelOr } from '../../lib/job-label';
import { jobStatusLabel, jobStatusSolid, jobStatusStyle } from '../../lib/job-status';
import { formatCalendarDate, formatInstantTime } from '../../lib/calendar-date';
import { fmtHours } from '../../lib/display-labels';
import { shiftHoursText } from '../../lib/shift-hours';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { isWorkerEditableStatus } from '../../types/timesheet-self-edit';
import { useBack } from '../../hooks/useBack';
import { timesheetStatusLabel, timesheetStatusTone } from '../../lib/timesheet-status';
import { useAuth } from '../../contexts/AuthContext';
import { useMyWork, type MyWorkItem } from '../../hooks/useMyWork';
import { CLOCK_LINKABLE_VERTICALS, clockLinkableItems } from '../../lib/work-item-selection';
import { pickerTitle, pickerMeta, sortPickerJobs, pickerSearchText, CLOCK_IN_JOBS_KEY, NO_ASSIGNED_JOBS_TEXT } from '../../lib/job-picker';
import { classifyLoadFailure } from '../../lib/load-failure';
import { type ClockTapResult } from '../../lib/offline/clock-queue';
import { clockQueuedText, useClockTaps } from '../../lib/offline/use-clock-queue';

// ─── SVG Progress Ring ───────────────────────────────────────────
function ProgressRing({ radius, stroke, progress, color }: { radius: number; stroke: number; progress: number; color: string }) {
  const norm = radius - stroke / 2;
  const circ = 2 * Math.PI * norm;
  const offset = circ - (Math.min(progress, 1) * circ);
  return (
    <svg width={radius * 2} height={radius * 2} className="absolute inset-0" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={radius} cy={radius} r={norm} fill="none" style={{ stroke: color }} strokeOpacity={0.1} strokeWidth={stroke} />
      <circle
        cx={radius} cy={radius} r={norm} fill="none"
        style={{ stroke: color, transition: 'stroke-dashoffset 1s linear' }} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={offset}
      />
    </svg>
  );
}

// ─── Own-shift helpers ───────────────────────────────────────────

/**
 * One of the worker's own rows from GET /timesheets/my/period/:periodId —
 * `SELECT t.*` plus the pay-code join, raw snake_case. Only the columns this
 * screen reads are named; the row carries more.
 */
interface OwnShift {
  id: string;
  entry_date: string;
  status: string;
  clock_in_at: string | null;
  clock_out_at: string | null;
  raw_minutes: number | string | null;
  /**
   * Minutes between the ROUNDED punches (api#823). NULL on every shift closed
   * while rounding was off, which means "use raw_minutes". Read only through
   * shiftHoursText / minutesOfRecord.
   */
  payable_minutes?: number | string | null;
  worker_note?: string | null;
  work_location?: string | null;
  /** Present on GET /timesheets/:id; the period list is own rows only anyway. */
  employee_id?: string;
  /**
   * Why the office rejected it, and its optional note to the worker (api#981).
   * The API sends `rejection_note` only while the row is rejected, and both
   * are cleared when the worker resubmits.
   */
  rejection_reason?: string | null;
  rejection_note?: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Absent, null or blank text is an em dash, never an empty line. */
const textOrDash = (v: string | null | undefined): string => (v && v.trim() ? v.trim() : '—');

/**
 * WHAT THE OFFICE SAID, AND WHAT TO DO ABOUT IT.
 *
 * A rejected shift is the one status only the worker can move: resubmit is
 * owner-only. Before api#981 the reason was stored and shown to nobody, so a
 * rejected shift looked like any other row with a red pill. Both fields always
 * render, the note as an em dash when the office left none, so "no note" is
 * visibly a fact and not a gap in the screen.
 */
function RejectedShiftNotice({
  shift,
  onResubmit,
  resubmitting,
}: {
  shift: OwnShift;
  onResubmit: () => void;
  resubmitting: boolean;
}) {
  return (
    <div
      role="group"
      aria-label="Rejected by the office"
      className="mt-3 rounded-lg px-3 py-2.5 text-sm border-l-4"
      style={{ background: 'var(--ci-red-wash)', borderLeftColor: C.error, color: C.onSurface }}
    >
      <dl className="space-y-1">
        <div className="flex gap-2">
          <dt className="shrink-0 w-12" style={{ color: C.onSurfaceVariant }}>Reason</dt>
          <dd className="font-medium">{textOrDash(shift.rejection_reason)}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0 w-12" style={{ color: C.onSurfaceVariant }}>Note</dt>
          <dd className="whitespace-pre-line break-words">{textOrDash(shift.rejection_note)}</dd>
        </div>
      </dl>
      <p className="mt-2 text-xs" style={{ color: C.onSurfaceVariant }}>
        Fix the shift if it needs it, then resubmit it for approval.
      </p>
      <button
        type="button"
        onClick={onResubmit}
        disabled={resubmitting}
        className="mt-2 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
        style={{ background: 'var(--ci-confirm)', color: 'var(--ci-on-confirm)' }}
      >
        <MI name={resubmitting ? 'progress_activity' : 'send'} size={16} className={resubmitting ? 'animate-spin' : ''} />
        Resubmit
      </button>
    </div>
  );
}


// ─── Component ───────────────────────────────────────────────────

/** What the sheet says when a Switch is refused — readable, never a raw code. */
function switchJobRefusalMessage(err: any): string {
  const status = err?.response?.status;
  if (status === 409) return "You're not clocked in any more — refresh and try again.";
  if (status === 404) return "That job isn't available any more. Pick another one.";
  if (status === 400) return "That job can't be linked to your shift.";
  return 'Could not switch the job. Check your connection and try again.';
}

/** Shown when the tenant has no pay period for today (pay-periods/current 404, or clock-in's 409). */
export const NO_PAY_SCHEDULE_TEXT = 'No pay schedule is set up for your company yet — ask your office';

export default function ClockInScreen() {
  const goBack = useBack('/field');
  // Every clock time on this screen — shown, and written back from the
  // clock-out picker — is the TENANT's wall clock (companies.time_zone).
  const timeZone = useTenantTimeZone();
  const location = useLocation();
  const { pathname } = location;

  /**
   * ── THE WAY OUT OF THIS SCREEN ─────────────────────────────────────────
   *
   * This screen is mounted at `/field/clock-in` for field crews and at
   * `/payroll/clock-in` for the office. The office mount sits inside AppLayout
   * and has a sidebar; the field mount has never had a header of its own and
   * was escaped through the bottom tab bar, which is gone. Without this the
   * screen is a dead end for the user it matters most to — a hand who taps
   * Clock In and then decides not to.
   *
   * Path-scoped so the office mount is left exactly as it was: adding a
   * control there would be a change to office navigation, which this work does
   * not touch. The internal back control on the "This period" view above is a
   * different thing — it returns to this view, not to another screen.
   */
  const onFieldRoute = pathname.startsWith('/field');
  const qc = useQueryClient();
  const { hasVertical, user } = useAuth();
  // Positive guards, one per vertical. The casing picker is not called at all
  // without casing — when it read GET /jobs (casing-gated) a PT-only tech got
  // a 403 and an empty sheet.
  const hasCasing = hasVertical('casing_running');
  // The verticals whose jobs come from GET /field/my-work (pressure testing,
  // earthworks), not from the casing picker.
  const hasWorkItems = CLOCK_LINKABLE_VERTICALS.some((v) => hasVertical(v));

  /**
   * ── ?timesheet=<id>: THE LINK IN THE REJECTION TEXT ──────────────────────
   *
   * api#981 texts a rejected worker `/field/clock-in?timesheet=<id>`. That
   * shift opens directly, read by id rather than found in the period list,
   * because a rejection can land after the period has rolled over. GET
   * /timesheets/:id 404s on "no such row" AND on "not yours" for a hand, but
   * an approver opening their own Clock In with a colleague's link would be
   * SHOWN that colleague's row, so the owner is checked here too: this screen
   * is the reader's own shifts and nothing else.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get('timesheet');

  // ── Local state ──
  // No default, deliberately. This is the one field the feature exists to
  // collect, and defaulting it would write a location nobody chose onto a
  // record that feeds a safety denominator. Clock In stays disabled until the
  // hand picks — same rule as rendering an absent value as `—` rather than 0,
  // applied to input instead of display.
  const [workLocation, setWorkLocation] = useState<WorkLocation | null>(null);
  const [selectedJobId, setSelectedJobId] = useState('');
  // Non-casing work (a PT job) picked instead of a casing job. Exactly one of
  // the two is ever set; a casing tenant never sets this one.
  const [selectedWorkItem, setSelectedWorkItem] = useState<MyWorkItem | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showProjectSheet, setShowProjectSheet] = useState(false);
  const [jobSearch, setJobSearch] = useState('');
  const [clockOutNote, setClockOutNote] = useState('');
  const [clockOutPhotos, setClockOutPhotos] = useState<string[]>([]);
  const [toast, setToast] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  // `submitted` is what the API said happened AFTER the hours landed. Clock-out
  // used to leave every row in `draft` — the office's Pending tab never saw
  // them — while this card said "Hours submitted" regardless. The API now
  // submits through the real path and reports whether it did; the card reads
  // that, and an absent field is NOT read as success.
  const [successData, setSuccessData] = useState<{ hours: string; submitted: boolean; submissionError: string | null } | null>(null);
  const [gpsLabel, setGpsLabel] = useState('Detecting...');
  const [successCountdown, setSuccessCountdown] = useState(3);
  /*
    `?view=period` opens "This period" directly. It is what the own-shift edit
    page and the missed-shift page return to, so their back arrow lands on the
    list the hand came from rather than on the clock.
  */
  const [showPeriodView, setShowPeriodView] = useState(() => !!linkedId || searchParams.get('view') === 'period');
  const photoInputRef = useRef<HTMLInputElement>(null);

  // ── Queries ──
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['timesheet-status'],
    queryFn: () => api.timesheets.status().then((r) => r.data?.data ?? r.data),
    refetchInterval: 30000,
  });

  const { data: payPeriod, error: payPeriodError } = useQuery({
    queryKey: ['pay-period-current'],
    queryFn: () => api.payPeriods.current().then((r) => r.data?.data ?? r.data),
  });
  // 404 is the API's answer for "this tenant has no period covering today" —
  // a setup state the office fixes, not a fault. Said plainly, once.
  const noPaySchedule = (payPeriodError as any)?.response?.status === 404;

  // Only the hand's own crewed, still-open jobs (api#1039) — it read GET /jobs,
  // every job in the company, completed ones included.
  const { data: jobs, isSuccess: jobsLoaded } = useQuery({
    queryKey: CLOCK_IN_JOBS_KEY,
    queryFn: () => api.timesheets.clockInJobs().then((r) => r.data?.data ?? r.data ?? []),
    enabled: hasCasing,
  });

  // The hand's own PT and earthworks jobs (GET /field/my-work), only the ones
  // the API says can be linked. Labels and statuses are the API's.
  const { data: myWork } = useMyWork('open', { enabled: hasWorkItems });
  const ptItems = clockLinkableItems(myWork?.items, hasVertical);

  /**
   * The hand's own shifts this period. The "This period" list used to read
   * `statusData.recentTimesheets`, a key GET /timesheets/status has never
   * sent, so it rendered the empty state on every tenant since it shipped.
   * It reads the endpoint built for it now. Gated on the current pay period's
   * id — with no period there is no list, and that renders as the empty
   * state it is, not as a guess.
   */
  const periodId: string | undefined = payPeriod?.id;
  const { data: myShifts, isLoading: myShiftsLoading, error: myShiftsError } = useQuery<OwnShift[]>({
    queryKey: ['timesheets', 'my-period', periodId],
    queryFn: () => api.timesheets.myPeriod(periodId as string).then((r) => {
      const body: any = r.data;
      const rows = body && typeof body === 'object' && 'data' in body ? body.data : body;
      return Array.isArray(rows) ? rows : [];
    }),
    enabled: !!periodId,
  });

  const linkedIdValid = !!linkedId && UUID_RE.test(linkedId);
  const linkedQuery = useQuery<OwnShift | null>({
    queryKey: ['timesheets', 'one', linkedId],
    queryFn: () => api.timesheets.get(linkedId as string).then((r) => {
      const body: any = r.data;
      const row = body && typeof body === 'object' && 'data' in body ? body.data : body;
      return row && typeof row === 'object' ? (row as OwnShift) : null;
    }),
    enabled: linkedIdValid,
    // A 404 is an answer (gone, or not yours), as is a refusal. Retry neither.
    retry: (n, err: any) => {
      const st = err?.response?.status;
      return st !== 404 && st !== 400 && st !== 401 && st !== 403 && n < 2;
    },
  });
  const linkedStatus: number | undefined = (linkedQuery.error as any)?.response?.status;
  const linkedShift: OwnShift | null =
    linkedQuery.data && !!user?.id && linkedQuery.data.employee_id === user.id
      ? linkedQuery.data
      : null;
  /** The one line shown in place of the linked shift when it cannot be shown. */
  const linkedProblem: string | null = !linkedId
    ? null
    : !linkedIdValid || linkedStatus === 404 || linkedStatus === 400 ||
      (linkedQuery.data !== undefined && !linkedShift)
      ? "That shift isn't one of yours, or it no longer exists."
      : linkedQuery.error
        ? classifyLoadFailure(linkedQuery.error) === 'refused'
          ? "You don't have permission to view that shift."
          : "Couldn't load that shift. Check your connection and try again."
        : null;

  // ── GPS label ──
  useEffect(() => {
    if (!navigator.geolocation) { setGpsLabel('GPS unavailable'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGpsLabel(`${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`),
      () => setGpsLabel('Location access denied'),
      { timeout: 8000, enableHighAccuracy: false },
    );
  }, []);

  // ── Elapsed timer ──
  // Derived from `clock_in_at`, not from the floored `elapsedMinutes` this
  // screen used to seed a local counter with. The seed re-ran on every mount
  // and every 30s poll, so the number fell back to the last whole minute each
  // time — visibly, because this screen and the home card share the query key
  // and a hand hops between them. See useShiftElapsed.
  // ── Clock taps go through the device queue (useClockTaps) ──
  // Shared with the home card. While taps are queued, the last one decides what
  // the hand sees; the server's status takes over when the queue is empty.
  const {
    pending: pendingTaps, shiftOnlyOnDevice, status: tapStatus,
    clockInAt: shownClockInAt, tapClockIn, tapClockOut,
  } = useClockTaps(user?.id, statusData);
  const onTheClock = tapStatus === 'active' || tapStatus === 'standby';
  const elapsed = useShiftElapsed(onTheClock ? shownClockInAt : null);

  // ── Mutations ──
  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  // How the last clock-out was recorded, for the success card. 'sent' is the
  // ordinary case; 'earlier' a replay whose first attempt had already landed
  // (its answer was lost, so the card must not claim submitted either way);
  // 'device' a tap that is safe in the queue and not yet at the server.
  const [clockOutRecorded, setClockOutRecorded] = useState<'sent' | 'earlier' | 'device'>('sent');

  const clockInMut = useMutation({
    mutationFn: async () => {
      // The tap time is NOW, before the GPS wait, which can take seconds.
      const tappedAt = new Date().toISOString();
      const gps = await getGPS();
      return tapClockIn({
          // NO `pay_code_id`, on any tenant — see the note above `currentPayCode`'s removal.
          // Non-null by the time this runs — the button is disabled without it.
          ...(workLocation ? { work_location: workLocation } : {}),
          ...(gps ? { lat: gps.lat, lng: gps.lng } : {}),
          // SHOP time has no job. Not merely hidden in the UI: the id is not sent,
          // so a stale selection from a previous FIELD shift cannot attach a well
          // to bench work.
          ...(workLocation === 'field' && selectedJobId ? { job_id: selectedJobId } : {}),
          // A PT job is a work item, never a job_id (api#976 refuses both).
          ...(workLocation === 'field' && selectedWorkItem
            ? { work_item_vertical: selectedWorkItem.vertical, work_item_id: selectedWorkItem.id }
            : {}),
      }, tappedAt);
    },
    onError: (err: any) => showToast(
      err.response?.data?.error === 'No open pay period found'
        ? NO_PAY_SCHEDULE_TEXT
        : err.response?.data?.error || err.message || 'Clock in failed',
    ),
    // Every outcome re-reads the shift, so the screen shows what the server holds.
    onSettled: () => { qc.invalidateQueries({ queryKey: ['timesheet-status'] }); },
  });

  // ── Switch: re-link the OPEN shift (api#999) ──
  // Picking in the job sheet while on the clock used to change only what this
  // screen showed; nothing reached the server. It now moves the shift itself
  // and the server audits who moved it, from which job to which.
  const switchMut = useMutation({
    mutationFn: (body: { job_id?: string; work_item_vertical?: string; work_item_id?: string }) =>
      api.timesheets.switchJob(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheet-status'] });
      qc.invalidateQueries({ queryKey: ['timesheets', 'my-period'] });
    },
    onError: (err: any) => showToast(switchJobRefusalMessage(err)),
  });

  const clockOutMut = useMutation({
    mutationFn: async () => {
      const tappedAt = new Date().toISOString();
      // Closes the shift BY NAME (useClockTaps), or chains to a queued clock-in.
      const { result: r, timesheetId: closedId } = await tapClockOut(
        { ...(clockOutNote ? { note: clockOutNote } : {}) }, tappedAt,
      );
      // Whether the shift reached the approval queue: read it, rather than
      // assume it. Unknown stays unknown and the card says "not submitted".
      let submitted = false;
      if (r.outcome === 'applied' && closedId) {
        try {
          const t: any = await api.timesheets.get(closedId);
          const row = t?.data && typeof t.data === 'object' && 'data' in t.data ? t.data.data : t?.data;
          submitted = row?.status === 'submitted';
        } catch { /* unknown — the card says so */ }
      }
      return { r, submitted };
    },
    onSuccess: ({ r, submitted }: { r: ClockTapResult; submitted: boolean }) => {
      setClockOutRecorded(r.outcome === 'queued' ? 'device' : r.outcome === 'already_done' ? 'earlier' : 'sent');
      // The figure is CAPTURED into successData below rather than frozen by
      // stopping a timer — there is no timer to stop now, and the capture is
      // what makes the card survive the status refetch that follows.
      setSuccessData({
        hours: formatElapsed(elapsed),
        submitted,
        submissionError: null,
      });
      setShowConfirmModal(false);
      setClockOutNote('');
      setClockOutPhotos([]);
      setShowSuccess(true);
      setSuccessCountdown(3);
    },
    onError: (err: any) => showToast(err.response?.data?.error || err.message || 'Clock out failed'),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['timesheet-status'] }); },
  });

  const standbyMut = useMutation({
    mutationFn: () => api.timesheets.standby(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['timesheet-status'] }); },
    onError: (err: any) => showToast(err.response?.data?.error || 'Standby failed'),
  });

  /*
    ── THE OWN-SHIFT EDIT AND THE MISSED SHIFT ARE PAGES, NOT SHEETS ─────────
    The edit used to be a bottom sheet over this list. Forms are their own
    pages now (operator, 2026-10-22): `<this mount>/shift/:id/edit` and
    `<this mount>/missed-shift`, beside each of the three ClockInScreen routes.
    Both return here with `?view=period`, carried as `listUrl` the way the
    record pages do (pages/downhole/recordPage.ts).
  */
  const navigate = useNavigate();
  const clockInBase = pathname.replace(/\/+$/, '');
  const periodListUrl = () => {
    const q = new URLSearchParams({ view: 'period' });
    if (linkedId) q.set('timesheet', linkedId);
    return `${clockInBase}?${q.toString()}`;
  };
  const openEdit = (row: OwnShift) =>
    navigate(`${clockInBase}/shift/${row.id}/edit`, { state: { listUrl: periodListUrl() } });
  const openMissedShift = () =>
    navigate(`${clockInBase}/missed-shift`, { state: { listUrl: periodListUrl() } });

  /* What a page sent back with — "Shift updated" — shown once, as the sheet's toast was. */
  const flash = (location.state as { flash?: unknown } | null)?.flash;
  useEffect(() => {
    if (typeof flash === 'string' && flash) showToast(flash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const resubmitMut = useMutation({
    mutationFn: (row: OwnShift) =>
      // The route writes worker_note from the body, so send the one it has.
      api.timesheets.resubmit(row.id, { worker_note: row.worker_note ?? '' }),
    onSuccess: (_res, row) => {
      qc.invalidateQueries({ queryKey: ['timesheets', 'my-period'] });
      // This shift only: a sibling link that 404'd has no reason to refetch.
      qc.invalidateQueries({ queryKey: ['timesheets', 'one', row.id] });
      showToast('Resubmitted for approval');
    },
    onError: (err: any) => showToast(
      err?.response?.status === 404
        ? 'This shift is no longer rejected, so it cannot be resubmitted.'
        : err?.response?.data?.error || "Couldn't resubmit. Check your connection and try again.",
    ),
  });

  const resumeMut = useMutation({
    mutationFn: () => api.timesheets.resume(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['timesheet-status'] }); },
    onError: (err: any) => showToast(err.response?.data?.error || 'Resume failed'),
  });

  const isMutating = clockInMut.isPending || clockOutMut.isPending || standbyMut.isPending || resumeMut.isPending;

  // ── Success countdown ──
  useEffect(() => {
    if (!showSuccess) return;
    if (successCountdown <= 0) {
      setShowSuccess(false);
      setSuccessData(null);
      qc.invalidateQueries({ queryKey: ['timesheet-status'] });
      return;
    }
    const t = setTimeout(() => setSuccessCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showSuccess, successCountdown, qc]);

  // ── Photo capture ──
  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setClockOutPhotos((prev) => [...prev, reader.result as string]);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Derived ──
  const status = tapStatus as 'clocked_out' | 'active' | 'standby';
  // ── There is deliberately no `currentPayCode` here either. ────────────────
  //
  // The pay code was a picker on this screen and a label in four places below
  // it. #343 took the arithmetic off this path — `clockOut()` records HOURS and
  // nothing else — and what was left was a rate that decides no pay, shown at
  // the moment a hand starts work. Same shape as the estimate removed below:
  // the number was real, the implication was not.
  //
  // It rendered on exactly one tenant. `pay_codes` has rows for Smith, the
  // demo, and no one else, so #437's "hide when empty" made this a control that
  // appeared only on the tenant you would show someone. Now it is gone
  // everywhere and the payload sends no `pay_code_id` at all.
  //
  // Two of those four labels also had no absent case: they read
  // `{name} · ${default_rate}/{unit}` with no guard, so on every tenant but
  // Smith they had already been rendering a bare `· $/day` — a currency symbol
  // and a unit with no number between them. That is the silent-blank-field
  // family, and it is why they went with the picker rather than after it.
  //
  // Historical labels are unaffected: `pay_code_id` on existing rows is
  // untouched, and both list readers LEFT JOIN `pay_codes`, so Smith's two
  // draft rows still show "Day Rate" in TimesheetDetailModal.

  // ── There is deliberately no `estimatedPay` here. ──────────────────────────
  //
  // This screen used to render `(elapsed / 3600) * baseRate`, ticking once a
  // second, where baseRate was `statusData?.timesheet?.base_rate ||
  // currentPayCode?.default_rate`. Two things were wrong with it:
  //
  //   1. `timesheets` has no `base_rate` column, so the first operand was
  //      ALWAYS undefined and it always fell through to the pay code's
  //      default_rate — which for a day-rate code is a rate per DAY. Seven
  //      seconds into an $875.00/day shift the screen read "Est. $1.70 earned";
  //      twenty seconds in, "$4.86". Day rates do not accrue per second. The
  //      label two lines below it said "$875.00/day" at the same time.
  //
  //   2. Fixing the arithmetic would not fix the screen. Most Canadian casing
  //      hands are paid a PERCENTAGE OF THE TICKET — hours are tracked for
  //      safety and scheduling, and pay comes from what the ticket billed. For
  //      them no dollar figure on a stopwatch has a correct value, because the
  //      number that decides their pay does not exist yet while they are on the
  //      clock.
  //
  // The API stopped persisting a pay figure at clock-out for the same reason
  // (see timesheets.service.ts). A hand's actual pay is on /field/pay, which
  // reads the ticket-percentage system. Duration is what this screen knows, so
  // duration is what it shows.

  const periodLabel = payPeriod
    ? `${formatCalendarDate(payPeriod.period_start, { month: 'short', day: 'numeric' })} – ${formatCalendarDate(payPeriod.period_end, { month: 'short', day: 'numeric' })}`
    : '';

  const clockInRaw = shownClockInAt;
  const clockedInAt = clockInRaw ? (formatInstantTime(clockInRaw, timeZone, { hour: 'numeric', minute: '2-digit', hour12: true }, 'en-US') ?? '') : '';

  // Progress for ring (fraction of 12-hour max)
  // No elapsed time means an empty progress arc, not a full one and not a
  // NaN sweep that renders as nothing at all.
  const ringProgress = elapsed === null ? 0 : Math.min(elapsed / (12 * 3600), 1);

  /**
   * The rows the picker actually renders, in picker order.
   *
   * Matched on everything the row shows and the well/location it may not —
   * see `pickerSearchText`. The number stays typeable with or without `J-`,
   * the original complaint on this screen being that it was missing.
   */
  const jobMatches = (job: any, q: string) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return pickerSearchText(job).includes(needle);
  };
  const visibleJobs = sortPickerJobs((jobs || []).filter((j: any) => jobMatches(j, jobSearch)));
  const itemMatches = (item: MyWorkItem, q: string) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return [item.number, item.title, item.statusLabel].filter(Boolean).join(' ').toLowerCase().includes(needle);
  };
  const visibleItems = ptItems.filter((i) => itemMatches(i, jobSearch));
  const pickerTotal = (jobs || []).length + ptItems.length;
  const pickerVisible = visibleJobs.length + visibleItems.length;

  const selectedJob = (jobs || []).find((j: any) => j.id === selectedJobId);
  const activeJob = statusData?.timesheet?.job_id ? (jobs || []).find((j: any) => j.id === statusData.timesheet.job_id) : null;
  // "General / No job" is current when the open shift has no link at all;
  // before clocking in it is current when nothing has been picked.
  const generalRowActive = onTheClock
    ? !statusData?.timesheet?.job_id && !statusData?.timesheet?.work_item_id
    : !selectedJobId && !selectedWorkItem;
  // A shift on a PT job carries the job's number snapshotted by the API
  // (timesheets.job_number, api#976); a casing shift has no work item and
  // falls through to '—' exactly as before.
  const activeJobFallback: string = statusData?.timesheet?.work_item_id
    ? ([statusData.timesheet.job_number, statusData.timesheet.job_name].filter(Boolean).join(' · ') || '—')
    : '—';

  // Both sources are absent today — GET /timesheets/status sends no
  // `monthlyHours` and pay_periods has no `total_hours` column — so this used
  // to read "0h" on every tenant. Absent is a dash, never 0 (CLAUDE.md #1).
  const monthlyHoursRaw = statusData?.monthlyHours ?? payPeriod?.total_hours;
  const monthlyHours: number | null =
    monthlyHoursRaw === null || monthlyHoursRaw === undefined || monthlyHoursRaw === '' || !Number.isFinite(Number(monthlyHoursRaw))
      ? null
      : Number(monthlyHoursRaw);
  const otLimit = statusData?.otLimit ?? payPeriod?.ot_threshold ?? 191;

  // ── Loading ──
  if (statusLoading) {
    return (
      <div className="min-h-full font-body pb-[var(--bottom-chrome)] ci-dark-override" style={{ background: C.bg }}>
        
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <MI name="hourglass_top" size={40} className="animate-spin" style={{ color: C.primary }} />
        </div>
      </div>
    );
  }

  // ── This period (own shifts) ──
  // Reachable from the success card AND from the idle screen. It used to sit
  // inside the success branch only, so the one route to a hand's own shifts
  // closed itself three seconds after clock-out — and a hand who forgot to
  // tap out yesterday never had it at all.
  if (showPeriodView) {
    return (
      <div className="min-h-full font-body pb-[var(--bottom-chrome)] ci-dark-override" style={{ background: C.bg, color: C.onSurface }}>

        <div className="px-5 pt-4 pb-6">
          <button
            aria-label="Back"
            onClick={() => {
              setShowPeriodView(false); setShowSuccess(false); setSuccessData(null);
              qc.invalidateQueries({ queryKey: ['timesheet-status'] });
              // Leaving the linked shift drops the link, or Back would reopen it;
              // `view` likewise, or a reload would land back on this list.
              if (linkedId || searchParams.get('view')) {
                setSearchParams((p) => { p.delete('timesheet'); p.delete('view'); return p; }, { replace: true });
              }
            }}
            className="mb-4"
            style={{ color: C.onSurfaceVariant }}
          >
            <MI name="arrow_back" size={24} />
          </button>
          <h1 className="font-headline text-2xl font-semibold" style={{ color: C.onSurface }}>This period</h1>
          {periodLabel && <p className="text-sm mt-1" style={{ color: C.onSurfaceVariant }}>{periodLabel}</p>}
        </div>
        <div className="px-5 grid grid-cols-3 gap-3 mb-6">
          {[
            { label: 'Hours', value: monthlyHours === null ? '—' : `${Math.round(monthlyHours)}h`, color: C.onSurface },
            { label: 'OT hours', value: statusData?.otToday != null ? `${fmtHours(statusData.otToday, 1)}h` : '—', color: C.primaryInk },
            { label: 'Shifts', value: myShifts ? String(myShifts.length) : '—', color: C.secondaryInk },
          ].map((s, i) => (
            <div key={i} role="group" aria-label={s.label} className="rounded-2xl p-4 text-center" style={{ background: C.surfaceLow }}>
              <div className="text-2xl font-bold font-headline" style={{ color: s.color }}>{s.value}</div>
              <div className="text-[11px] mt-1 uppercase tracking-wider" style={{ color: C.onSurfaceVariant }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div className="px-5 space-y-2">
          {/*
            A shift the clock never saw (api#1148): the hand forgot to tap in at
            all. Its own page; it is submitted for someone else to approve.
          */}
          <button
            type="button"
            onClick={openMissedShift}
            className="w-full mb-2 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold"
            style={{ border: '1px solid var(--ci-edge-25)', color: C.onSurface, background: C.surface }}
          >
            <MI name="more_time" size={18} />
            Add a missed shift
          </button>
          {linkedProblem && (
            <p role="status" className="rounded-xl px-4 py-3 text-sm" style={{ background: C.surface, color: C.onSurface, border: '1px solid var(--ci-edge-25)' }}>
              {linkedProblem}
            </p>
          )}
          {linkedId && linkedIdValid && linkedQuery.isLoading && (
            <div className="flex justify-center py-6">
              <MI name="hourglass_top" size={28} className="animate-spin" style={{ color: C.primary }} />
            </div>
          )}
          {/*
            The linked shift leads, marked, and is not repeated below it. It may
            belong to an earlier period, so it is not assumed to be in the list.
          */}
          {(linkedShift ? [linkedShift, ...(myShifts ?? []).filter((r) => r.id !== linkedShift.id)] : (myShifts ?? [])).map((ts) => {
            // Offer the edit only where the API will take it: the worker's own
            // row (this list is nothing else), in draft / rejected / submitted,
            // and CLOSED. An open shift has no clock-out to correct — that is
            // the Clock out button's job, and a PUT with clock_out_at on an open
            // row would close it through a side door.
            const editable = isWorkerEditableStatus(ts.status) && !!ts.clock_out_at;
            return (
              <div
                key={ts.id}
                role="listitem"
                aria-label={`Shift ${ts.entry_date}`}
                aria-current={ts.id === linkedShift?.id ? 'true' : undefined}
                className="rounded-xl p-4 border"
                style={{ background: C.surfaceLow, borderColor: ts.id === linkedShift?.id ? C.primary : 'var(--ci-hair-05)' }}
              >
                {ts.id === linkedShift?.id && (
                  <p className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: C.primaryInk }}>From your link</p>
                )}
                <div className="flex justify-between mb-1">
                  <span className="font-medium">{formatCalendarDate(ts.entry_date, { weekday: 'short', month: 'short', day: 'numeric' }) ?? '—'}</span>
                  <span className="font-bold font-headline">{shiftHoursText(ts.payable_minutes, ts.raw_minutes)}</span>
                </div>
                <div className="flex justify-between items-center text-sm" style={{ color: C.onSurfaceVariant }}>
                  <span>{formatInstantTime(ts.clock_in_at, timeZone) ?? '—'} – {formatInstantTime(ts.clock_out_at, timeZone) ?? '—'}</span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    data-tone={timesheetStatusTone(ts.status)}
                    style={{
                      background: `var(--ci-pill-${timesheetStatusTone(ts.status)})`,
                      color: `var(--ci-pill-${timesheetStatusTone(ts.status)}-ink)`,
                    }}
                  >{timesheetStatusLabel(ts.status)}</span>
                </div>
                {ts.status === 'rejected' && (
                  <RejectedShiftNotice
                    shift={ts}
                    onResubmit={() => resubmitMut.mutate(ts)}
                    resubmitting={resubmitMut.isPending && resubmitMut.variables?.id === ts.id}
                  />
                )}
                {editable && (
                  <button
                    type="button"
                    onClick={() => openEdit(ts)}
                    className="mt-3 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
                    style={{ background: C.surfaceHighest, color: C.primaryInk }}
                  >
                    <MI name="edit" size={16} />
                    Edit times
                  </button>
                )}
              </div>
            );
          })}
          {myShiftsLoading && (
            <div className="flex justify-center py-12">
              <MI name="hourglass_top" size={32} className="animate-spin" style={{ color: C.primary }} />
            </div>
          )}
          {/*
            A refusal is not an empty state. "No timesheets this period" on a
            failed read would tell a hand their hours are gone.
          */}
          {!myShiftsLoading && myShiftsError && (
            <div className="text-center py-12" style={{ color: C.errorInk }}>
              <MI name="error" size={40} className="mb-2 opacity-60" />
              <p>Couldn't load your shifts. Check your connection and try again.</p>
            </div>
          )}
          {!myShiftsLoading && !myShiftsError && (myShifts ?? []).length === 0 && !linkedShift && (
            <div className="text-center py-12" style={{ color: C.onSurfaceVariant }}>
              <MI name="event_note" size={40} className="mb-2 opacity-40" />
              <p>{periodId ? 'No timesheets this period' : 'No pay period is set up yet'}</p>
            </div>
          )}
        </div>

        {/* Same box as the main render's toast; green because this one reports a save, not a failure. */}
        {toast && (
          <div
            className="fixed bottom-[var(--fab-bottom)] left-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-medium text-center"
            style={{ background: C.secondaryContainer, color: C.onSecondaryContainer }}
          >
            {toast}
          </div>
        )}
      </div>
    );
  }

  // ── Success ──
  if (showSuccess && successData) {
    return (
      <div className="min-h-full font-body flex flex-col pb-[var(--bottom-chrome)] ci-dark-override" style={{ background: C.bg, color: C.onSurface }}>
        
        <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="ci-scale-in mb-6">
          <div className="w-24 h-24 rounded-full border-2 flex items-center justify-center" style={{ borderColor: C.secondary }}>
            <MI name="check" size={48} filled className="ci-scale-in" style={{ color: C.secondary }} />
          </div>
        </div>
        <h2 className="font-headline text-2xl font-semibold mb-1">Shift complete</h2>
        <p className="text-sm mb-8" style={{ color: C.onSurfaceVariant }}>Great work today</p>

        <div className="rounded-2xl p-5 w-full max-w-sm space-y-3" style={{ background: C.surfaceLow }}>
          <div className="flex justify-between text-sm">
            <span style={{ color: C.onSurfaceVariant }}>Duration</span>
            <span className="font-semibold">{successData.hours}</span>
          </div>
          <div className="flex justify-between text-sm pt-2 border-t" style={{ borderColor: 'var(--ci-hair-06)' }}>
            <span style={{ color: C.onSurfaceVariant }}>Recorded</span>
            {clockOutRecorded === 'device' ? (
              <span className="font-semibold" style={{ color: C.secondaryInk }}>Saved on this device</span>
            ) : clockOutRecorded === 'earlier' ? (
              <span className="font-semibold" style={{ color: C.secondaryInk }}>Saved on an earlier try</span>
            ) : successData.submitted ? (
              <span className="font-semibold" style={{ color: C.secondaryInk }}>Hours submitted</span>
            ) : (
              <span className="font-semibold" style={{ color: C.errorInk }}>Saved, not submitted</span>
            )}
          </div>
          {clockOutRecorded === 'device' && (
            <p className="text-xs text-left" style={{ color: C.onSurfaceVariant }}>
              Not saved yet — retrying. Your clock-out is held on this device with the time you tapped, and it goes to the office as soon as the server confirms it.
            </p>
          )}
          {clockOutRecorded === 'earlier' && (
            <p className="text-xs text-left" style={{ color: C.onSurfaceVariant }}>
              Your first tap reached the office but the reply was lost. Check your shifts to see it was sent for approval.
            </p>
          )}
          {clockOutRecorded === 'sent' && !successData.submitted && (
            <p className="text-xs text-left" style={{ color: C.onSurfaceVariant }}>
              {successData.submissionError ?? 'Your hours are recorded but were not sent for approval. Let the office know.'}
            </p>
          )}
        </div>

        <p className="text-sm mt-6 mb-4" style={{ color: C.onSurfaceVariant }}>
          Returning to home in {successCountdown}s...
        </p>

        <button
          onClick={() => setShowPeriodView(true)}
          className="px-6 py-3 rounded-xl text-sm font-medium border"
          style={{ borderColor: 'var(--ci-hair-20)', color: C.onSurface }}
        >
          View My Week
        </button>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <div className="min-h-full font-body flex flex-col pb-[var(--bottom-chrome)] ci-dark-override" style={{ background: C.bg, color: C.onSurface }}>
      {/* Field mount only — see `onFieldRoute`. */}
      {onFieldRoute && (
        <div className="px-5 pt-4">
          <button
            aria-label="Back"
            onClick={goBack}
            style={{ color: C.onSurfaceVariant }}
          >
            <MI name="arrow_back" size={24} />
          </button>
        </div>
      )}

      {/* ── Content ── */}
      <div className="flex-1 flex flex-col px-5 pb-4 max-w-lg mx-auto w-full overflow-y-auto">

        {/* ══════ STATE 1 — NOT CLOCKED IN ══════ */}
        {status === 'clocked_out' && (
          <div className="flex-1 flex flex-col">
            {/* Hero */}
            <div className="flex flex-col items-center mt-8 mb-8">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: C.surfaceLow, boxShadow: `0 0 40px ${mix(C.primary, 8.235)}` }}>
                <MI name="hourglass_top" size={40} style={{ color: C.primary }} />
              </div>
              <h1 className="font-headline text-2xl font-semibold mb-1">Not clocked in</h1>
              <p className="text-sm" style={{ color: C.onSurfaceVariant }}>Ready when you are</p>
            </div>

            {/* Config card */}
            <div className="rounded-2xl p-4 space-y-4 mb-6" style={{ background: C.surfaceLow }}>
              {/* Where — SHOP or FIELD. The primary control, above the job. */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: C.onSurfaceVariant }}>Where</label>
                <div className="grid grid-cols-2 gap-2">
                  {WORK_LOCATIONS.map((loc) => {
                    const selected = workLocation === loc;
                    return (
                      <button
                        key={loc}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setWorkLocation(loc)}
                        className="rounded-xl px-4 py-3 text-sm font-semibold transition-colors"
                        style={selected
                          ? { background: C.secondaryContainer, color: C.onSecondaryContainer, border: `1px solid ${C.secondaryContainer}` }
                          : { background: C.bg, color: C.onSurface, border: '1px solid var(--ci-edge-25)' }}
                      >
                        {WORK_LOCATION_LABELS[loc]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/*
                The job picker is FIELD-only. Shop time has no job, and a visible
                picker on a shop shift invites a hand to attach a well to bench
                work. Hidden until FIELD is chosen — which also means it is
                hidden before any choice is made, so the first thing on screen is
                the question this feature exists to ask.
              */}
              {workLocation === 'field' && (
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: C.onSurfaceVariant }}>Job</label>
                  <button
                    onClick={() => { setJobSearch(''); setShowProjectSheet(true); }}
                    className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors"
                    style={{ background: C.bg, border: '1px solid var(--ci-edge-25)' }}
                  >
                    <MI name="architecture" size={20} style={{ color: C.primary }} />
                    <span className="flex-1 text-sm font-medium truncate" style={{ color: C.onSurface }}>
                      {selectedWorkItem
                        ? (selectedWorkItem.number ?? selectedWorkItem.title ?? 'Selected job')
                        : jobLabelOr(selectedJob, 'Select job')}
                    </span>
                    <MI name="chevron_right" size={20} style={{ color: C.onSurfaceVariant }} />
                  </button>
                </div>
              )}
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-[40px]" />

            {/* No pay period covers today. Said before the hand taps, in words,
                rather than as a 404 or a raw 409 after. Rendered only on that
                answer, so a tenant with a period sees nothing new. */}
            {noPaySchedule && (
              <p data-testid="clockin-no-pay-schedule" className="mb-3 rounded-xl px-4 py-3 text-sm text-center"
                 style={{ background: C.bg, border: '1px solid var(--ci-edge-25)', color: C.onSurfaceVariant }}>
                {NO_PAY_SCHEDULE_TEXT}
              </p>
            )}

            {/*
              Clock In button.

              GATED ON `workLocation`, NOT ON THE PAY CODE. The condition here
              was `!selectedPayCodeId`, and that is what made this button
              permanently dead for every tenant except the Smith demo: no pay
              codes means no eligible codes means the id never leaves '', so the
              button sat at 40% opacity forever with no error and nothing to
              click. The pay code is gone from this screen entirely now — see
              the note where `currentPayCode` used to be — so there is no longer
              a second thing this could accidentally be gated on.

              It is gated at all because `workLocation` has no default and must
              not acquire one — see the state declaration.
            */}
            <button
              onClick={() => clockInMut.mutate()}
              disabled={!workLocation || isMutating}
              className="w-full rounded-2xl py-5 flex items-center justify-center gap-3 font-semibold text-lg disabled:opacity-40 transition-all active:scale-[0.97]"
              style={{ background: C.secondaryContainer, color: C.onSecondaryContainer }}
            >
              {clockInMut.isPending ? (
                <MI name="progress_activity" size={24} className="animate-spin" />
              ) : (
                <>
                  <MI name="play_circle" size={28} filled />
                  <span>Clock In</span>
                </>
              )}
            </button>
            {pendingTaps.length > 0 && <ClockQueuedNote count={pendingTaps.length} />}
            <div className="flex items-center justify-center gap-1.5 mt-3 mb-2">
              <MI name="location_on" size={14} style={{ color: C.onSurfaceVariant }} />
              <span className="text-xs" style={{ color: C.onSurfaceVariant }}>Current Location: {gpsLabel}</span>
            </div>
            {/*
              The way to a hand's own shifts from the screen they are on when
              they are NOT on the clock — which is when a forgotten tap-out
              needs fixing. Reads "My shifts", not "Edit": most rows are
              approved and there is nothing to edit on those.
            */}
            <button
              type="button"
              onClick={() => setShowPeriodView(true)}
              className="w-full flex items-center justify-center gap-1.5 py-3 text-sm font-medium"
              style={{ color: C.primaryInk }}
            >
              <MI name="event_note" size={18} />
              My shifts this period
            </button>
          </div>
        )}

        {/* ══════ STATE 2 — ACTIVE ══════ */}
        {status === 'active' && (
          <div className="flex-1 flex flex-col">
            {/* Status pill */}
            <div className="flex justify-center mt-3 mb-6">
              <div className="flex items-center gap-2 rounded-full px-4 py-1.5" style={{ background: 'var(--ci-active-pill)', border: '1px solid var(--ci-active-pill-edge)' }}>
                <span className="w-2 h-2 rounded-full ci-pulse-dot" style={{ background: 'var(--ci-active-pill-ink)' }} />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--ci-active-pill-ink)' }}>Active</span>
              </div>
            </div>

            {/* Timer circle */}
            <div className="flex justify-center mb-2">
              <div className="relative" style={{ width: 192, height: 192 }}>
                <ProgressRing radius={96} stroke={3} progress={ringProgress} color={C.secondary} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <MI name="schedule" size={52} filled style={{ color: C.secondary }} />
                </div>
                {/* Pulse dot top */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1">
                  <span className="block w-3 h-3 rounded-full ci-pulse-dot" style={{ background: C.secondary, boxShadow: `0 0 8px ${mix(C.secondary, 50.196)}` }} />
                </div>
              </div>
            </div>

            {/* Timer text */}
            <div className="text-center mb-1">
              <div className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: C.onSurfaceVariant }}>Elapsed Time</div>
              <div className="font-headline font-light text-6xl tracking-tight" style={{ fontVariantNumeric: 'tabular-nums', color: C.onSurface }}>
                {formatElapsed(elapsed)}
              </div>
              <div className="text-sm mt-2" style={{ color: C.onSurfaceVariant }}>
                Clocked in since {clockedInAt || '—'}
              </div>
            </div>

            {/* Info cards */}
            <div className="space-y-2 mt-6 mb-4">
              {/* Clocked in card */}
              <div className="rounded-2xl p-4 flex items-center justify-between" style={{ background: C.surfaceLow }}>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: C.onSurfaceVariant }}>Clocked In</div>
                  <div className="font-semibold">{formatInstantTime(shownClockInAt, timeZone, { hour: 'numeric', minute: '2-digit', hour12: true }, 'en-US') ?? '—'}</div>
                </div>
                <button
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
                  style={{ background: C.surfaceHighest, color: C.primaryInk }}
                  onClick={() => { setJobSearch(''); setShowProjectSheet(true); }}
                  disabled={shiftOnlyOnDevice}
                >
                  <MI name="swap_horiz" size={16} />
                  Switch
                </button>
              </div>

              {/* Current assignment card */}
              <div className="rounded-2xl p-4" style={{ background: C.surfaceLow }}>
                <div className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: C.primaryInk }}>Current Assignment</div>
                <div className="font-semibold text-sm">
                  {jobLabelOr(activeJob, activeJobFallback)}
                </div>
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-[16px]" />

            {/* Action grid */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <button
                onClick={() => standbyMut.mutate()}
                disabled={isMutating || shiftOnlyOnDevice}
                className="h-16 rounded-2xl flex items-center justify-center gap-2 font-medium text-sm disabled:opacity-40 active:scale-[0.97] transition-all"
                style={{ background: C.surfaceHighest, border: '1px solid var(--ci-edge-30)', color: C.onSurface }}
              >
                {standbyMut.isPending ? (
                  <MI name="progress_activity" size={20} className="animate-spin" />
                ) : (
                  <>
                    <MI name="pause_circle" size={22} style={{ color: C.primary }} />
                    <span>On standby</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={isMutating}
                className="h-16 rounded-2xl flex items-center justify-center gap-2 font-medium text-sm disabled:opacity-40 active:scale-[0.97] transition-all"
                style={{ background: C.surfaceHighest, border: '1px solid var(--ci-edge-30)', color: C.onSurface }}
              >
                <MI name="logout" size={22} style={{ color: C.error }} />
                <span>Clock out</span>
              </button>
            </div>
            {pendingTaps.length > 0 && <ClockQueuedNote count={pendingTaps.length} />}
          </div>
        )}

        {/* ══════ STATE 3 — STANDBY ══════ */}
        {status === 'standby' && (
          <div className="flex-1 flex flex-col">
            {/* Icon circle */}
            <div className="flex justify-center mt-6 mb-4">
              <div className="relative">
                <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: C.surfaceLow, boxShadow: `0 0 0 4px ${mix(C.primary, 10.196)}` }}>
                  <MI name="schedule" size={48} filled style={{ color: C.primary }} />
                </div>
                <span className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full ci-pulse-dot" style={{ background: C.primary, boxShadow: `0 0 8px ${mix(C.primary, 50.196)}` }} />
              </div>
            </div>

            {/* Status pill */}
            <div className="flex justify-center mb-4">
              <div className="flex items-center gap-2 rounded-full px-4 py-1.5" style={{ background: 'var(--ci-standby-pill)', border: '1px solid var(--ci-standby-pill-edge)' }}>
                <span className="w-2 h-2 rounded-full ci-pulse-dot" style={{ background: 'var(--ci-standby-pill-ink)' }} />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--ci-standby-pill-ink)' }}>On Standby</span>
              </div>
            </div>

            {/* Timer */}
            <div className="text-center mb-6">
              <div className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: C.onSurfaceVariant }}>Elapsed Standby Time</div>
              <div className="font-headline font-light text-6xl tracking-tight" style={{ fontVariantNumeric: 'tabular-nums', color: C.onSurface }}>
                {formatElapsed(elapsed)}
              </div>
              {/*
                NO "Active time" ROW. It read `statusData.activeTime`, a key
                GET /timesheets/status has never sent — `getCurrentStatus`
                returns status, timesheet, standbySince and elapsedMinutes and
                nothing else, and the only `activeTime` in the API is an
                unrelated `activeTimeOff` on the employee route. So the guard
                was never true and this never rendered on any tenant, which is
                the same shape as the `recentTimesheets` key this screen was
                already wrong about once. Removed rather than repaired: the
                active/standby split is not currently on the payload, and a
                second wrong duration next to a correct one is worse than one
                correct one alone.
              */}
            </div>

            {/* Info card */}
            <div className="rounded-2xl p-4 flex gap-3" style={{ background: C.surfaceLow }}>
              <div className="w-1 rounded-full self-stretch" style={{ background: mix(C.primary, 50.196) }} />
              <div className="flex-1">
                <div className="text-xs mb-1" style={{ color: C.onSurfaceVariant }}>Clocked in {clockedInAt || '—'}</div>
                <div className="text-sm font-semibold" style={{ color: C.primaryInk }}>
                  Standby since {clockedInAt || '—'}
                </div>
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-[24px]" />

            {/* Buttons */}
            <button
              onClick={() => resumeMut.mutate()}
              disabled={isMutating}
              className="w-full rounded-2xl py-4 flex items-center justify-center gap-2.5 font-semibold text-base disabled:opacity-40 active:scale-[0.97] transition-all mb-3"
              style={{ background: 'var(--ci-go-fill)', color: C.onSecondaryContainer }}
            >
              {resumeMut.isPending ? (
                <MI name="progress_activity" size={22} className="animate-spin" />
              ) : (
                <>
                  <MI name="play_arrow" size={24} filled />
                  <span>Resume active</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={isMutating}
              className="w-full rounded-2xl py-4 flex items-center justify-center gap-2.5 font-medium text-sm disabled:opacity-40 active:scale-[0.97] transition-all"
              style={{ border: '1px solid var(--ci-edge-30)', color: C.onSurface, background: 'transparent' }}
            >
              <MI name="logout" size={22} style={{ color: C.error }} />
              <span>Clock out</span>
            </button>
          </div>
        )}
      </div>

      {/* ══════ CONFIRM CLOCK OUT — Bottom Sheet ══════ */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowConfirmModal(false); setClockOutNote(''); setClockOutPhotos([]); } }}
        >
          <div className="ci-sheet-enter ci-dark-override w-full max-w-lg" style={{ background: C.surface, colorScheme: 'var(--ci-scheme)', borderRadius: '32px 32px 0 0', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-4">
              <div className="w-12 h-1.5 rounded-full" style={{ background: 'var(--ci-hair-10)' }} />
            </div>

            <div className="px-6">
              <h2 className="font-headline text-xl font-semibold mb-5" style={{ color: C.onSurface }}>Clock out?</h2>

              {/* Summary rows */}
              <div className="space-y-3 mb-5">
                <div className="flex justify-between text-sm">
                  <span style={{ color: C.onSurfaceVariant }}>Total time</span>
                  <span className="font-semibold" style={{ color: C.onSurface }}>{elapsed === null ? '—' : `${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m`}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: C.onSurfaceVariant }}>Job</span>
                  <span className="font-semibold" style={{ color: C.onSurface }}>{jobLabelOr(activeJob, activeJobFallback)}</span>
                </div>
              </div>

              {/* Job notes */}
              <label className="block text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: C.onSurfaceVariant }}>Job notes</label>
              <textarea
                value={clockOutNote}
                onChange={(e) => setClockOutNote(e.target.value)}
                placeholder="What did you work on?"
                className="w-full rounded-xl px-4 py-3 text-sm resize-none"
                style={{ background: C.bg, border: '1px solid var(--ci-edge-25)', color: C.onSurface, minHeight: 120, outline: 'none' }}
              />

              {/* Site photos */}
              <label className="block text-[11px] font-semibold uppercase tracking-widest mt-4 mb-2" style={{ color: C.onSurfaceVariant }}>Site photos</label>
              <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoCapture} />
              <div className="flex gap-2 overflow-x-auto pb-2">
                {/* Add button */}
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center gap-0.5"
                  style={{ border: '1px dashed var(--ci-edge-38)', background: 'transparent' }}
                >
                  <MI name="add_a_photo" size={20} style={{ color: C.onSurfaceVariant }} />
                  <span className="text-[9px]" style={{ color: C.onSurfaceVariant }}>Add</span>
                </button>
                {/* Captured photos */}
                {clockOutPhotos.map((photo, i) => (
                  <div key={i} className="shrink-0 w-16 h-16 rounded-xl overflow-hidden relative">
                    <img src={photo} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => setClockOutPhotos((p) => p.filter((_, j) => j !== i))}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(0,0,0,0.7)' }}
                    >
                      <MI name="close" size={14} style={{ color: '#fff' }} />
                    </button>
                  </div>
                ))}
                {/* Empty placeholders */}
                {Array.from({ length: Math.max(0, 3 - clockOutPhotos.length) }).map((_, i) => (
                  <div key={`empty-${i}`} className="shrink-0 w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: C.bg, border: '1px solid var(--ci-edge-13)' }}>
                    <MI name="image" size={18} style={{ color: C.onSurfaceVariant, opacity: 0.3 }} />
                  </div>
                ))}
              </div>

              {/* Buttons */}
              <div className="space-y-2 mt-5">
                <button
                  onClick={() => clockOutMut.mutate()}
                  disabled={clockOutMut.isPending}
                  className="w-full rounded-xl py-4 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
                  style={{ background: 'var(--ci-confirm)', color: 'var(--ci-on-confirm)' }}
                >
                  {clockOutMut.isPending ? (
                    <MI name="progress_activity" size={20} className="animate-spin" />
                  ) : (
                    <span>Confirm clock out</span>
                  )}
                </button>
                {pendingTaps.length > 0 && <ClockQueuedNote count={pendingTaps.length} />}
                <button
                  onClick={() => { setShowConfirmModal(false); setClockOutNote(''); setClockOutPhotos([]); }}
                  className="w-full rounded-xl py-4 font-medium text-sm active:scale-[0.98] transition-all"
                  style={{ border: '1px solid var(--ci-edge-19)', color: C.onSurfaceVariant, background: 'transparent' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ PROJECT SWITCHER — Bottom Sheet ══════ */}
      {showProjectSheet && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowProjectSheet(false); }}
        >
          <div className="ci-sheet-enter ci-dark-override w-full max-w-lg max-h-[80vh] flex flex-col" style={{ background: C.surface, colorScheme: 'var(--ci-scheme)', borderRadius: '32px 32px 0 0', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-4 shrink-0">
              <div className="w-12 h-1.5 rounded-full" style={{ background: 'var(--ci-hair-10)' }} />
            </div>

            <div className="px-6 shrink-0">
              {/*
                "Switch job" was the title in BOTH states, including the one
                where a hand is choosing for the first time and has nothing to
                switch from. Same family as the label bug: the screen naming an
                action it is not performing.
              */}
              <h2 className="font-headline text-xl font-semibold mb-1" style={{ color: C.onSurface }}>
                {statusData?.timesheet?.job_id || statusData?.timesheet?.work_item_id ? 'Switch job' : 'Select job'}
              </h2>
              {/*
                The subtitle here read "Time logged to current project up to
                now" and no time was ever logged, shown or calculated. It is
                replaced with a count of what is actually in the list, which is
                the thing a hand scrolling 49 jobs (PAR) needs to know.
              */}
              <p className="text-sm mb-4" style={{ color: C.onSurfaceVariant }}>
                {jobSearch.trim()
                  ? `${pickerVisible} of ${pickerTotal} jobs`
                  : `${pickerTotal} jobs`}
              </p>

              {/* Search */}
              <div className="relative mb-4">
                <MI name="search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.onSurfaceVariant }} />
                <input
                  type="text"
                  placeholder="Search jobs"
                  aria-label="Search jobs"
                  value={jobSearch}
                  onChange={(e) => setJobSearch(e.target.value)}
                  className="w-full rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none"
                  style={{ background: C.bg, border: '1px solid var(--ci-edge-25)', color: C.onSurface }}
                />
              </div>
            </div>

            {/* Project list */}
            <div className="flex-1 overflow-y-auto px-6 space-y-1">
              {/* No project option */}
              <button
                onClick={() => {
                  if (onTheClock) switchMut.mutate({});
                  else { setSelectedJobId(''); setSelectedWorkItem(null); }
                  setShowProjectSheet(false);
                }}
                className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors"
                style={{ background: generalRowActive ? 'var(--ci-row-on)' : 'transparent' }}
              >
                <span className="w-3 h-3 rounded-full" style={{ background: 'var(--ci-nojob-dot)' }} />
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: C.onSurface }}>General / No job</div>
                  {/*
                    "0h logged today" used to sit here. It was a hardcoded
                    string on every row — never a number this screen fetched —
                    so it told a hand something false about his own day. There
                    is nothing true to put on this row, so nothing is.
                  */}
                </div>
                {generalRowActive && <MI name="check_circle" size={20} filled style={{ color: C.secondary }} />}
              </button>

              {visibleJobs.map((job: any) => {
                // On the clock, "current" is what the SHIFT holds; off it, the pick.
                const isActive = onTheClock
                  ? job.id === statusData?.timesheet?.job_id
                  : job.id === selectedJobId || job.id === statusData?.timesheet?.job_id;
                const title = pickerTitle(job);
                return (
                  <button
                    key={job.id}
                    data-testid="job-picker-row"
                    onClick={() => {
                      if (onTheClock) switchMut.mutate({ job_id: job.id });
                      else { setSelectedJobId(job.id); setSelectedWorkItem(null); }
                      setShowProjectSheet(false);
                    }}
                    className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors"
                    style={{ background: isActive ? 'var(--ci-row-on)' : 'transparent' }}
                  >
                    {/*
                      The dot was amber on every row whatever the status. It
                      now takes the status hue from the shared map, and the
                      chip beside the title says the word, so the colour is
                      never the only carrier.
                    */}
                    <span aria-hidden="true" data-testid="job-picker-dot" className={`w-3 h-3 rounded-full shrink-0 ${jobStatusSolid(job.status)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0 flex text-sm font-semibold" style={{ color: C.onSurface }}>
                          <span className="truncate">{title.lead}</span>
                          {title.rig !== null && <span className="shrink-0 whitespace-pre">{` · ${title.rig}`}</span>}
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${jobStatusStyle(job.status)}`}>
                          {jobStatusLabel(job.status)}
                        </span>
                      </div>
                      {/*
                        Real hours per job were considered for this line and
                        rejected (the `0h logged today` it once held was a
                        hardcoded lie): "today" would have to be resolved in the
                        tenant's zone and the open shift is not in `raw_minutes`
                        until clock-out. A second wrong number is worse than none.
                      */}
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 min-w-0 text-xs truncate" style={{ color: C.onSurfaceVariant }}>
                          {pickerMeta(job)}
                        </div>
                        {isActive && (
                          <span className="shrink-0 text-xs font-semibold" style={{ color: C.primaryInk }}>Current</span>
                        )}
                      </div>
                    </div>
                    {isActive && <MI name="check_circle" size={20} filled style={{ color: C.secondary }} />}
                  </button>
                );
              })}

              {/*
                A SEARCH THAT MATCHES NOTHING HAS TO SAY SO. Without this the
                sheet silently collapses to the "General / No job" row alone,
                which reads as "this tenant has no jobs" rather than "your
                typing matched none of them" — the same silent-empty state this
                screen has already been wrong about once.
              */}
              {/* The hand's pressure-testing and earthworks jobs. Number, "customer · location"
                  and status label are all the API's (GET /field/my-work). */}
              {visibleItems.map((item) => {
                const isActive = onTheClock
                  ? item.id === statusData?.timesheet?.work_item_id
                  : selectedWorkItem?.id === item.id || item.id === statusData?.timesheet?.work_item_id;
                return (
                  <button
                    key={`wi-${item.vertical}-${item.id}`}
                    data-testid="work-item-picker-row"
                    onClick={() => {
                      if (onTheClock) switchMut.mutate({ work_item_vertical: item.vertical, work_item_id: item.id });
                      else { setSelectedWorkItem(item); setSelectedJobId(''); }
                      setShowProjectSheet(false);
                    }}
                    className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors"
                    style={{ background: isActive ? 'var(--ci-row-on)' : 'transparent' }}
                  >
                    <span aria-hidden="true" className={`w-3 h-3 rounded-full shrink-0 ${item.active ? 'bg-green-500' : 'bg-fieldiq-border'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0 flex text-sm font-semibold" style={{ color: C.onSurface }}>
                          <span className="truncate">{item.number ?? '—'}</span>
                        </div>
                        <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider bg-fieldiq-dark text-fieldiq-text">
                          {item.statusLabel}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 min-w-0 text-xs truncate" style={{ color: C.onSurfaceVariant }}>
                          {item.title ?? '—'}
                        </div>
                        {isActive && (
                          <span className="shrink-0 text-xs font-semibold" style={{ color: C.primaryInk }}>Current</span>
                        )}
                      </div>
                    </div>
                    {isActive && <MI name="check_circle" size={20} filled style={{ color: C.secondary }} />}
                  </button>
                );
              })}

              {hasCasing && jobsLoaded && jobSearch.trim() === '' && pickerTotal === 0 && (
                <div data-testid="no-assigned-jobs" className="px-4 py-6 text-center text-sm" style={{ color: C.onSurfaceVariant }}>
                  {NO_ASSIGNED_JOBS_TEXT}
                </div>
              )}

              {jobSearch.trim() !== '' && pickerVisible === 0 && (
                <div className="px-4 py-6 text-center text-sm" style={{ color: C.onSurfaceVariant }}>
                  No job matches &ldquo;{jobSearch.trim()}&rdquo;
                </div>
              )}
            </div>

            <div className="px-6 pt-4 shrink-0">
              <button
                onClick={() => setShowProjectSheet(false)}
                className="w-full rounded-xl py-3.5 font-medium text-sm"
                style={{ border: '1px solid var(--ci-hair-10)', color: C.onSurfaceVariant }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div
          className="fixed bottom-[var(--fab-bottom)] left-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-medium text-center"
          style={{ background: 'var(--ci-toast-bad)', color: 'var(--ci-on-toast-bad)', boxShadow: `0 4px 16px ${mix(C.error, 25.098)}` }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

/** Shown while clock taps are saved on this device and not yet at the server. */
function ClockQueuedNote({ count }: { count: number }) {
  return (
    <p role="status" className="text-xs text-center mt-2 flex items-center justify-center gap-1.5" style={{ color: C.onSurfaceVariant }}>
      <MI name="cloud_off" size={14} />
      {clockQueuedText(count)}
    </p>
  );
}
